import type { Dispatch, JSX, MutableRefObject, ReactNode, SetStateAction } from 'react';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const NAV_MINIMIZED_STORAGE_KEY = 'feathr:nav:isMinimized';
const NAV_SECONDARY_MODE_STORAGE_KEY = 'feathr:nav:wasInSecondaryNavMode';
const NAV_WIDTH_EXPANDED = 240;
const NAV_WIDTH_MINIMIZED = 64;

type TSecondaryNav = 'home' | 'projects' | 'community' | 'settings' | null;
type TAnimationDirection = 'left' | 'right';
type NavigationProviderProps = { readonly children: ReactNode };

interface INavigationContext {
  isMinimized: boolean;
  activeSecondaryNav: TSecondaryNav;
  navWidth: number;
  isAnimating: boolean;
  animationDirection: TAnimationDirection;
  scrollableRef: MutableRefObject<HTMLDivElement | null>;
  isTransitioningFromSecondary: boolean;
  setIsMinimized: Dispatch<SetStateAction<boolean>>;
  setActiveSecondaryNav: (nav: TSecondaryNav) => void;
  setIsAnimating: Dispatch<SetStateAction<boolean>>;
  toggleMinimized: () => void;
  expandNavFromMinimized: () => void;
}

const NavigationContext = createContext<INavigationContext | undefined>(undefined);

function getInitialMinimizedState(): boolean {
  if (typeof globalThis.window === 'undefined') {
    return false;
  }
  const stored = localStorage.getItem(NAV_MINIMIZED_STORAGE_KEY);
  return stored === 'true';
}

function getInitialSecondaryNavModeState(): boolean {
  if (typeof globalThis.window === 'undefined') {
    return false;
  }
  const stored = localStorage.getItem(NAV_SECONDARY_MODE_STORAGE_KEY);
  return stored === 'true';
}

function getSecondaryNavFromPath(pathname: string): TSecondaryNav {
  const pathSegments = pathname.split('/');
  const primarySegment = pathSegments[2] || '';

  if (['accounts', 'dashboard', 'marketing'].includes(primarySegment)) {
    return 'home';
  }
  if (primarySegment === 'projects' || primarySegment === 'project') {
    return 'projects';
  }
  if (primarySegment === 'data') {
    return 'community';
  }
  if (primarySegment === 'settings') {
    return 'settings';
  }
  return null;
}

function getInitialPathname(): string {
  if (typeof window !== 'undefined') {
    return window.location.pathname;
  }
  return '';
}

function NavigationProvider({ children }: NavigationProviderProps): JSX.Element {
  const [isMinimized, setIsMinimized] = useState<boolean>(getInitialMinimizedState);
  const [activeSecondaryNav, setActiveSecondaryNav] = useState<TSecondaryNav>(() =>
    getSecondaryNavFromPath(getInitialPathname()),
  );
  const [wasInSecondaryNavMode, setWasInSecondaryNavMode] = useState<boolean>(
    getInitialSecondaryNavModeState,
  );
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const [animationDirection, setAnimationDirection] = useState<TAnimationDirection>('right');
  const [isTransitioningFromSecondary, setIsTransitioningFromSecondary] = useState<boolean>(false);
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(NAV_MINIMIZED_STORAGE_KEY, String(isMinimized));
  }, [isMinimized]);

  useEffect(() => {
    localStorage.setItem(NAV_SECONDARY_MODE_STORAGE_KEY, String(wasInSecondaryNavMode));
  }, [wasInSecondaryNavMode]);

  const toggleMinimized = useCallback((): void => {
    setIsMinimized((prev) => {
      if (!prev) {
        setWasInSecondaryNavMode(activeSecondaryNav !== null);
        if (activeSecondaryNav !== null) {
          setIsTransitioningFromSecondary(true);
          setTimeout(() => setIsTransitioningFromSecondary(false), 350);
        }
      }
      return !prev;
    });
  }, [activeSecondaryNav]);

  const handleSecondaryNavChange = useCallback(
    (nav: TSecondaryNav): void => {
      if (isAnimating) {
        return;
      }

      setAnimationDirection(nav === null ? 'left' : 'right');
      setIsAnimating(true);
      setActiveSecondaryNav(nav);
    },
    [isAnimating],
  );

  const expandNavFromMinimized = useCallback((): void => {
    if (!isMinimized) {
      return;
    }

    if (wasInSecondaryNavMode) {
      const currentSecondaryNav = getSecondaryNavFromPath(globalThis.window.location.pathname);
      if (currentSecondaryNav) {
        setAnimationDirection('right');
        setIsAnimating(true);
        setActiveSecondaryNav(currentSecondaryNav);
      }
    }
    setIsMinimized(false);
  }, [isMinimized, wasInSecondaryNavMode]);

  const navWidth = isMinimized ? NAV_WIDTH_MINIMIZED : NAV_WIDTH_EXPANDED;

  const memoizedValue = useMemo(() => {
    return {
      isMinimized,
      activeSecondaryNav,
      navWidth,
      isAnimating,
      animationDirection,
      scrollableRef,
      isTransitioningFromSecondary,
      setIsMinimized,
      setActiveSecondaryNav: handleSecondaryNavChange,
      setIsAnimating,
      toggleMinimized,
      expandNavFromMinimized,
    };
  }, [
    isMinimized,
    activeSecondaryNav,
    navWidth,
    isAnimating,
    animationDirection,
    isTransitioningFromSecondary,
    handleSecondaryNavChange,
    toggleMinimized,
    expandNavFromMinimized,
  ]);

  return <NavigationContext.Provider value={memoizedValue}>{children}</NavigationContext.Provider>;
}

function useNavigation(): INavigationContext {
  const context = useContext(NavigationContext);

  if (context === undefined) {
    throw new Error('useNavigation hook must be wrapped in a NavigationProvider context provider');
  }
  return context;
}

export {
  getSecondaryNavFromPath,
  NavigationProvider,
  useNavigation,
  NAV_WIDTH_EXPANDED,
  NAV_WIDTH_MINIMIZED,
};
export type { TSecondaryNav, TAnimationDirection };
